/**
 * AuthProvider — initializes auth state and subscribes to Supabase auth events.
 *
 * Wraps the app (in App.tsx). On mount:
 *  1. Fetches the current session from Supabase (handles refresh-on-load)
 *  2. Subscribes to onAuthStateChange so the store stays in sync
 *  3. Cleans up the subscription on unmount
 *
 * Children render immediately — components that need to wait for auth
 * resolution should check useAuthStore().isLoading themselves.
 */

import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { useAuthStore } from '@/store/useAuthStore';
import { useProjectStore } from '@/store/useProjectStore';
import { isNativePlatform } from '@/lib/native/platform';
import { initDeepLinks } from '@/lib/native/deepLinks';
import { initPushNotifications, onPushTap, clearPushBadge } from '@/lib/native/push';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);
  const bootstrap = useProjectStore((s) => s.bootstrap);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  // Native only: route incoming deep links (auth callbacks, universal links)
  // through the SPA router, and forward push taps to their target path.
  useEffect(() => {
    if (!isNativePlatform()) return;
    void initDeepLinks((path) => navigate(path));
    onPushTap((data) => {
      if (typeof data.path === 'string') navigate(data.path);
    });
  }, [navigate]);

  // Native only: once authenticated, register for push and clear the badge.
  useEffect(() => {
    if (!isNativePlatform() || !user) return;
    void initPushNotifications();
    void clearPushBadge();
  }, [user]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      // No backend configured — stay in localStorage mode
      setLoading(false);
      bootstrap(false);
      return;
    }

    let cancelled = false;
    let lastUserId: string | null = null;

    // Initial session load
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        const u = data.session?.user ?? null;
        setUser(u);
        setLoading(false);
        lastUserId = u?.id ?? null;
        // Bootstrap data based on initial auth state
        bootstrap(u !== null);
      })
      .catch((err) => {
        console.error('[ProjexHQ] Failed to load auth session:', err);
        if (cancelled) return;
        setLoading(false);
        bootstrap(false);
      });

    // Subscribe to auth state changes
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      // Only re-bootstrap when the actual user identity changes
      // (TOKEN_REFRESHED events shouldn't trigger reloads)
      const currentId = u?.id ?? null;
      if (currentId !== lastUserId) {
        lastUserId = currentId;
        bootstrap(u !== null);
      }
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [setUser, setLoading, bootstrap]);

  return <>{children}</>;
}
